/**
 * Complete / cancel / reopen a project or list - the one way the app does it, from every entry point
 * (project header, sidebar and task context menus, status picker, checkboxes on project rows,
 * keyboard shortcuts, drops on the Logbook).
 *
 * A project / list is never left closed with open to-dos in it: they would drop out of Today and
 * Upcoming without a trace. So closing one that still has open to-dos always asks what happens to
 * them (complete or cancel the rest) before anything is written. The shared op `closeContainer`
 * says whether it needs that answer; the dialog lives in `ProjectStatusDialogHost` (mounted once,
 * in ActionPanel), so callers whose own UI closes at once (context menus) can still ask. One request
 * per project; a bulk action queues them.
 *
 * Reopening reopens the container alone and then offers a toast to reopen the to-dos that were
 * closed together with it.
 */

import { create } from "zustand"
import { toast } from "sonner"
import i18next from "i18next"
import { closeContainer, isClosableType, reopenClosedWith, reopenContainer } from "@totodo/operations"
import type { RemainingTasks } from "@totodo/operations"
import { db, ObjectStatus } from "@/db/schema"
import type { DbObject } from "@/db/schema"

export interface ProjectStatusRequest {
  project: DbObject
  /** Completed or Canceled. */
  status: ObjectStatus.Completed | ObjectStatus.Canceled
  unfinished: number
}

interface ProjectStatusRequestState {
  queue: ProjectStatusRequest[]
  push: (request: ProjectStatusRequest) => void
  /** Drop the request being shown (answered or dismissed). */
  shift: () => void
}

export const useProjectStatusRequests = create<ProjectStatusRequestState>((set) => ({
  queue: [],
  // One question per project: a double click (or the same project twice in a bulk action) must not
  // queue a second dialog for it
  push: (request) =>
    set((s) =>
      s.queue.some((q) => q.project.id === request.project.id) ? s : { queue: [...s.queue, request] },
    ),
  shift: () => set((s) => ({ queue: s.queue.slice(1) })),
}))

/** The dialog's answer for the rest of the to-dos. */
export function remainingFromStatus(childStatus: ObjectStatus): RemainingTasks {
  return childStatus === ObjectStatus.Canceled ? "cancel" : "complete"
}

/** Close a project / list with the user's answer (the dialog's confirm). */
export async function closeProjectWith(
  projectId: string,
  status: ObjectStatus.Completed | ObjectStatus.Canceled,
  childStatus: ObjectStatus,
): Promise<void> {
  const result = await closeContainer(projectId, status, { remaining: remainingFromStatus(childStatus) })
  if (!result.ok) {
    console.warn("[projectStatus] close failed:", projectId, result.error)
    toast.error(result.error)
  }
}

/**
 * Reopen a project / list and, if to-dos were closed together with it, offer to reopen those too
 * in a toast. The ids and the old close stamp come from the reopen; the action goes through
 * reopenClosedWith, which re-checks every row (a toast ten seconds old cannot reopen something that
 * changed meanwhile) and registers one undo entry.
 */
export async function reopenProject(projectId: string): Promise<void> {
  const result = await reopenContainer(projectId)
  if (!result.ok) {
    console.warn("[projectStatus] reopen failed:", projectId, result.error)
    toast.error(result.error)
    return
  }
  const ids = result.data.closedWithIt
  const stamp = result.data.closedWithStamp
  if (ids.length === 0 || stamp === undefined) return
  const t = i18next.t.bind(i18next)
  const isProject = result.data.kind === "project"
  const message =
    ids.length === 1
      ? isProject
        ? t("{{count}} to-do was closed with this project. Reopen it too?", { count: ids.length })
        : t("{{count}} to-do was closed with this list. Reopen it too?", { count: ids.length })
      : isProject
        ? t("{{count}} to-dos were closed with this project. Reopen them too?", { count: ids.length })
        : t("{{count}} to-dos were closed with this list. Reopen them too?", { count: ids.length })
  toast(message, {
    duration: 10_000,
    action: {
      label: t("Reopen"),
      onClick: () => {
        void reopenClosedWith(ids, stamp)
      },
    },
  })
}

/**
 * Set the status of a project/list. Reopening, and closing one with nothing left open, apply at once;
 * otherwise the status dialog asks first and `closeContainer` runs on its answer.
 */
export async function setProjectStatus(projectId: string, status: ObjectStatus): Promise<void> {
  if (status === ObjectStatus.Unfinished) {
    await reopenProject(projectId)
    return
  }
  if (status !== ObjectStatus.Completed && status !== ObjectStatus.Canceled) return
  const result = await closeContainer(projectId, status)
  if (!result.ok) {
    console.warn("[projectStatus] close failed:", projectId, result.error)
    toast.error(result.error)
    return
  }
  if (result.data.done) return
  const project = await db.totodo.get(projectId)
  if (!project) return
  useProjectStatusRequests.getState().push({ project, status, unfinished: result.data.openTasks })
}

/**
 * Route a status change of one id: when it is a project / list, set it the container way
 * (asking when it must) and say so. A caller that handles any object (checkbox, picker, shortcut)
 * calls this first and does the task thing only when it returns false.
 */
export async function routeContainerStatus(id: string, status: ObjectStatus): Promise<boolean> {
  const obj = await db.totodo.get(id)
  if (!obj || !isClosableType(obj.type)) return false
  await setProjectStatus(id, status)
  return true
}

/**
 * Split a selection: the projects / lists among `ids` are set to `status` the container way
 * (queueing a question each when needed), the ids of everything else are returned for the caller's
 * task operation. Areas and sections are not containers in this sense and stay in the returned ids.
 */
export async function setContainerStatuses(ids: readonly string[], status: ObjectStatus): Promise<string[]> {
  const objects = await db.totodo.bulkGet([...ids])
  const rest: string[] = []
  const containers: string[] = []
  ids.forEach((id, i) => {
    const o = objects[i]
    if (o && isClosableType(o.type)) containers.push(id)
    else rest.push(id)
  })
  for (const id of containers) await setProjectStatus(id, status)
  return rest
}
